import { Maybe } from 'maybeasy';
import * as React from 'react';
import { RichTextEditor } from '../../../RichTextEditor';
import { NotTranslated, T } from '../../../Translations';
import { assertNever } from '@kofno/piper';
import DisabledNotice from './DisabledNotice';
import { CommunicationEntry, Draft, SaveState, SeedState } from './Types';

interface Props {
  entry: CommunicationEntry;
  draft: Draft;
  seedState: SeedState;
  canManage: boolean;
  saveState: SaveState;
  onDraftChange: (draft: Draft) => void;
  onSave: () => void;
  onRevert: Maybe<() => void>;
}

// The editor opens pre-filled with whatever the collection inherits today, so
// the admin edits the real message instead of guessing at it. Saying where the
// copy came from matters: without it, inherited text looks like an override
// that already exists.
const SeedNote: React.FC<{ state: SeedState }> = ({ state }) => {
  switch (state.kind) {
    case 'idle':
      return <></>;
    case 'loading':
      return (
        <p className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
          <T kind="Loading the message this collection sends today" />
        </p>
      );
    case 'applied':
      return (
        <p className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
          <T kind="Started from the message this collection sends today" />
        </p>
      );
    case 'unavailable':
      return (
        <p className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
          <T kind="The current message could not be loaded, so this editor starts empty" />
        </p>
      );
  }
  return assertNever(state);
};

const OverrideEditor: React.FC<Props> = ({
  entry,
  draft,
  seedState,
  canManage,
  saveState,
  onDraftChange,
  onSave,
  onRevert,
}) => {
  const isSaving = saveState.kind === 'saving';

  return (
    <div className="flex flex-col gap-4">
      <DisabledNotice disabled={entry.disabled} />
      <div>
        <label
          className="block text-muted-foreground mb-1"
          style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
          htmlFor="communication-override-subject"
        >
          <T kind="Subject" />
        </label>
        <input
          id="communication-override-subject"
          data-intercom-target="communication-subject-input-field"
          type="text"
          className="w-full border border-border rounded-md bg-input px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none text-foreground"
          style={{ fontSize: 'var(--text-sm)' }}
          value={draft.subject}
          readOnly={!canManage}
          onChange={(event) => onDraftChange({ ...draft, subject: event.target.value })}
        />
      </div>
      <SeedNote state={seedState} />
      <span
        className="no-tailwindcss-base block"
        data-intercom-target="communication-body-text-editor"
      >
        <RichTextEditor
          value={draft.content}
          onChange={(content) => onDraftChange({ ...draft, content })}
          readonly={!canManage}
          backgroundColor="var(--card)"
        />
      </span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label
            className="block text-muted-foreground mb-1"
            style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
            htmlFor="communication-override-cta-label"
          >
            <T kind="Button label" />
          </label>
          <input
            id="communication-override-cta-label"
            data-intercom-target="communication-cta-label-input-field"
            type="text"
            className="w-full border border-border rounded-md bg-input px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none text-foreground"
            style={{ fontSize: 'var(--text-sm)' }}
            value={draft.ctaLabel}
            readOnly={!canManage}
            onChange={(event) => onDraftChange({ ...draft, ctaLabel: event.target.value })}
          />
        </div>
        <div>
          <label
            className="block text-muted-foreground mb-1"
            style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)' }}
            htmlFor="communication-override-cta-url"
          >
            <T kind="Button link" />
          </label>
          <input
            id="communication-override-cta-url"
            data-intercom-target="communication-cta-url-input-field"
            type="text"
            className="w-full border border-border rounded-md bg-input px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none text-foreground"
            style={{ fontSize: 'var(--text-sm)' }}
            value={draft.ctaUrl}
            readOnly={!canManage}
            onChange={(event) => onDraftChange({ ...draft, ctaUrl: event.target.value })}
          />
        </div>
      </div>
      <p className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
        <T kind="The button only appears when both a label and a link are set" />
      </p>
      {canManage && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="px-3 py-1.5 bg-accent text-accent-foreground rounded transition-all hover:opacity-90 disabled:opacity-50"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            disabled={isSaving}
            onClick={onSave}
            data-intercom-target="save-override-button"
          >
            <T kind="Save override" />
          </button>
          {onRevert
            .map((revert) => (
              <button
                type="button"
                className="px-3 py-1.5 bg-background text-foreground border border-border rounded transition-all hover:bg-muted disabled:opacity-50"
                style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                disabled={isSaving}
                onClick={revert}
                data-intercom-target="revert-override-button"
              >
                <T kind="Revert to inherited" />
              </button>
            ))
            .getOrElse(() => (
              <></>
            ))}
          {saveState.kind === 'saved' && (
            <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
              <T kind="Saved" />
            </span>
          )}
          {saveState.kind === 'errored' && (
            <span className="text-destructive" style={{ fontSize: 'var(--text-xs)' }}>
              <T kind="Something went wrong" />
            </span>
          )}
        </div>
      )}
      {entry.override
        .map(() => <></>)
        .getOrElse(() =>
          entry.organizationSubject
            .map((subject) => (
              <p className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
                <T kind="Organization" />
                <NotTranslated text=": " />
                <NotTranslated text={subject} />
              </p>
            ))
            .getOrElse(() => <></>),
        )}
    </div>
  );
};

export default OverrideEditor;
